import { useRef, useState } from "react";
import { Input } from "./Input";
import { toast } from "./Toast";

/** A short inline rename session; Enter/blur save once, Escape restores the original. */
export function RenameInput({
  initial,
  label,
  onSave,
  onClose,
  className,
}: {
  initial: string;
  label: string;
  onSave: (value: string) => Promise<unknown>;
  onClose: () => void;
  className?: string;
}) {
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const finished = useRef(false);
  const commit = async () => {
    if (finished.current) return;
    finished.current = true;
    const name = value.trim();
    if (!name || name === initial) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await onSave(name);
      onClose();
    } catch (error) {
      finished.current = false;
      setSaving(false);
      toast({
        title: "Could not rename",
        description: error instanceof Error ? error.message : String(error),
        variant: "danger",
      });
    }
  };
  return (
    <Input
      autoFocus
      aria-label={label}
      className={className}
      value={value}
      readOnly={saving}
      aria-busy={saving}
      onFocus={(event) => event.currentTarget.select()}
      onChange={(event) => setValue(event.target.value)}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      onBlur={() => void commit()}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === "Enter" && !event.nativeEvent.isComposing) {
          event.preventDefault();
          void commit();
        }
        if (event.key === "Escape" && !saving) {
          event.preventDefault();
          finished.current = true;
          onClose();
        }
      }}
    />
  );
}
